import React, { useEffect, useState, useCallback } from "react";

const FontFamilyDropdown: React.FC<{ editor: any }> = ({ editor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentFontName, setCurrentFontName] = useState("Default");

  const fonts = [
    { name: "Default", value: "" },
    { name: "Arial", value: "Arial, sans-serif" },
    { name: "Helvetica", value: "Helvetica, sans-serif" },
    { name: "Times New Roman", value: '"Times New Roman", serif' },
    { name: "Georgia", value: "Georgia, serif" },
    { name: "Courier New", value: '"Courier New", monospace' },
    { name: "Verdana", value: "Verdana, sans-serif" },
    { name: "Tahoma", value: "Tahoma, sans-serif" },
    { name: "Comic Sans MS", value: '"Comic Sans MS", cursive' },
    { name: "Inter", value: "Inter, sans-serif" },
    { name: "Roboto", value: "Roboto, sans-serif" },
    { name: "Open Sans", value: '"Open Sans", sans-serif' },
    { name: "Lato", value: "Lato, sans-serif" },
    { name: "Montserrat", value: "Montserrat, sans-serif" },
    { name: "Poppins", value: "Poppins, sans-serif" },
    { name: "Source Sans Pro", value: '"Source Sans Pro", sans-serif' },
    { name: "Nunito", value: "Nunito, sans-serif" },
    { name: "Ubuntu", value: "Ubuntu, sans-serif" },
    { name: "Raleway", value: "Raleway, sans-serif" },
    { name: "Work Sans", value: '"Work Sans", sans-serif' },
    { name: "Playfair Display", value: '"Playfair Display", serif' },
    { name: "Merriweather", value: "Merriweather, serif" },
    { name: "Lora", value: "Lora, serif" },
    { name: "Source Code Pro", value: '"Source Code Pro", monospace' },
    { name: "JetBrains Mono", value: '"JetBrains Mono", monospace' },
    { name: "Dancing Script", value: '"Dancing Script", cursive' },
    { name: "Pacifico", value: "Pacifico, cursive" },
    { name: "Lobster", value: "Lobster, cursive" },
    { name: "Bebas Neue", value: '"Bebas Neue", cursive' },
  ];

  const getCurrentFont = useCallback(() => {
    if (!editor) return "Default";

    // Check if there's an active textStyle mark with fontFamily
    if (editor.isActive("textStyle")) {
      const attributes = editor.getAttributes("textStyle");
      if (attributes.fontFamily) {
        // Find matching font by value
        const matchingFont = fonts.find(
          (font) => font.value === attributes.fontFamily
        );
        if (matchingFont) {
          return matchingFont.name;
        }

        // If no exact match, try to extract font name from the fontFamily string
        const fontFamily = attributes.fontFamily.toString();
        const extractedName = fontFamily
          .split(",")[0]
          .replace(/['"]/g, "")
          .trim();
        return extractedName || "Custom Font";
      }
    }

    // Check each font to see if it's active
    for (const font of fonts) {
      if (editor.isActive("textStyle", { fontFamily: font.value })) {
        return font.name;
      }
    }

    return "Default";
  }, [editor, fonts]);

  // Update current font when editor changes
  useEffect(() => {
    const updateCurrentFont = () => {
      setCurrentFontName(getCurrentFont());
    };

    if (editor) {
      editor.on("selectionUpdate", updateCurrentFont);
      editor.on("transaction", updateCurrentFont);

      // Initial update
      updateCurrentFont();
    }

    return () => {
      if (editor) {
        editor.off("selectionUpdate", updateCurrentFont);
        editor.off("transaction", updateCurrentFont);
      }
    };
  }, [editor, getCurrentFont]);

  const truncateFontName = (name: string, maxLength: number = 12) => {
    return name.length > maxLength
      ? name.substring(0, maxLength) + "..."
      : name;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isOpen && !target.closest(".dropdown")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="dropdown font-family-dropdown">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-sm btn-outline-secondary d-flex align-items-center px-3 text-truncate"
        style={{ minWidth: "120px", maxWidth: "120px" }}
        title={`Font Family: ${currentFontName}`}>
        <span
          className="small fs-15 text-truncate"
          style={{ maxWidth: "85px" }}>
          {truncateFontName(currentFontName)}
        </span>
        <i className="ti ti-chevron-down ms-1 fs-12 flex-shrink-0"></i>
      </button>

      {isOpen && (
        <ul
          className="dropdown-menu show position-absolute mt-1"
          style={{
            minWidth: "180px",
            maxHeight: "300px",
            overflowY: "auto",
            zIndex: 1000,
          }}>
          {fonts.map((font, index) => (
            <li key={index}>
              <button
                onClick={() => {
                  if (font.value) {
                    editor.chain().focus().setFontFamily(font.value).run();
                  } else {
                    editor.chain().focus().unsetFontFamily().run();
                  }
                  setIsOpen(false);
                }}
                className={`dropdown-item ${
                  currentFontName === font.name ? "active" : ""
                }`}
                style={{ fontFamily: font.value || "inherit" }}>
                {font.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FontFamilyDropdown;
